import React, { useEffect, useRef, useState } from "react";

import AddButton from "../shared-components/AddButton";
import { ButtonColorTypes, ButtonTypes } from "../shared-components/Button";
import Button from "../shared-components/Button";
import { useReportsWizard } from "./ReportsWizardProvider";
import {
  useDeletePersonDocument,
  useGetPersonDocuments,
  useGetPersonDocumentUploadUrl,
  useUploadFileToS3,
} from "../api/360FilesAPI";
import { PersonDocument } from "../api/360Files";
import { PaperClipIcon, XCircleIcon } from "@heroicons/react/24/outline";

type UploadedFileProps = {
  doc: PersonDocument;
  deleteHandler: (document_type: string, filename: string) => void;
};

function UploadedFile({ doc, deleteHandler }: UploadedFileProps) {
  return (
    <div className="flex gap-4 m-2">
      <PaperClipIcon className="w-5" />
      {doc.filename}
      <XCircleIcon
        className="w-5"
        onClick={() => deleteHandler(doc.type, doc.filename)}
      />
    </div>
  );
}

type Props = {};

export const ReportsWizardDocumentUpload = (props: Props) => {
  const {
    nextStep,
    previousStep,
    getChecktypeInfo,
    ensurePersonAvailable,
    personUUID,
  } = useReportsWizard();
  const { mutateAsync: getPersonDocumentUploadUrl } =
    useGetPersonDocumentUploadUrl();
  const { mutateAsync: uploadFileToS3 } = useUploadFileToS3();
  const { mutateAsync: getPersonDocuments, data: personDocuments } =
    useGetPersonDocuments();
  const [localDocs, setLocalDocs] = useState<PersonDocument[]>([]);
  const {
    mutateAsync: deletePersonDocument,
    isLoading: deletePersonDocumentLoading,
  } = useDeletePersonDocument();

  const isValid = true;
  const [isUploadingConsent, setIsUploadingConsent] = useState<boolean>(false);
  const [isUploadingOther, setIsUploadingOther] = useState<boolean>(false);
  const checktypeInfo = getChecktypeInfo();
  const consentInput = useRef<HTMLInputElement>(null);
  const otherInput = useRef<HTMLInputElement>(null);

  const showConsentForm =
    (checktypeInfo?.selectedThreeSixtyChecktype.length ?? 0) > 0;
  const showOtherDocuments =
    (checktypeInfo?.selectedThreeSixtyChecktype.length ?? 0) > 0;

  useEffect(() => {
    if (personUUID) {
      getPersonDocuments({ personUuid: personUUID });
    }
  }, [personUUID, getPersonDocuments]);

  useEffect(() => {
    if (personDocuments) {
      setLocalDocs(personDocuments);
    }
  }, [personDocuments, setLocalDocs]);

  const consentDocs: PersonDocument[] = (localDocs ?? []).filter(
    (doc) => doc.type === "consent-form"
  );
  const otherDocs: PersonDocument[] = (localDocs ?? []).filter(
    (doc) => doc.type === "other"
  );

  const handleNextStep = () => {
    if (isValid) {
      nextStep(isValid, true);
    }
  };

  async function uploadFile(
    file: File,
    document_type: string
  ): Promise<boolean> {
    if (!personUUID) {
      return false;
    }
    const personDocumentUploadUrl = await getPersonDocumentUploadUrl({
      person_uuid: personUUID,
      document_type: document_type,
      filename: file.name,
    });
    await uploadFileToS3({
      uploadUrl: personDocumentUploadUrl.upload_url,
      file: file,
    });
    return true;
  }

  const consentAddHandler = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { files } = event.target as HTMLInputElement;
    if (files && files.length > 0 && !isUploadingConsent) {
      setIsUploadingConsent(true);
      for (const file of files) {
        await uploadFile(file, "consent-form");
      }
      setIsUploadingConsent(false);
      getPersonDocuments({ personUuid: personUUID ?? "" });
    }
  };

  const otherAddHandler = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { files } = event.target as HTMLInputElement;
    if (files && files.length > 0 && !isUploadingOther) {
      setIsUploadingOther(true);
      for (const file of files) {
        await uploadFile(file, "other");
      }
      setIsUploadingOther(false);
      getPersonDocuments({ personUuid: personUUID ?? "" });
    }
  };

  const deleteHandler = async (document_type: string, filename: string) => {
    deletePersonDocument({
      personUuid: personUUID ?? "",
      document_type: document_type,
      filename: filename,
    });
    let temp = localDocs.filter(
      (doc) => doc.type !== document_type || doc.filename !== filename
    );
    setLocalDocs(temp);
  };

  const disableInputs =
    deletePersonDocumentLoading || isUploadingConsent || isUploadingOther;

  return (
    <div>
      <span className="text-lg font-bold text-darkBlue">Document Upload</span>
      <div className="gap-2 p-4 border border-gray-200 rounded-lg mt-4 bg-gray-100">
        {showConsentForm && (
          <div>
            <div className="flex-1 pb-2 mt-6 font-bold">Consent Form</div>
            <div>
              {consentDocs.map((doc) => (
                <UploadedFile
                  key={doc.url}
                  doc={doc}
                  deleteHandler={deleteHandler}
                />
              ))}
              <AddButton
                text={isUploadingConsent ? "Uploading..." : "Upload File"}
                disabled={disableInputs}
                onClick={() =>
                  ensurePersonAvailable() &&
                  consentInput &&
                  consentInput.current &&
                  consentInput.current.click()
                }
              />
              <input
                className="hidden"
                type="file"
                ref={consentInput}
                name="_file_consent"
                onChange={consentAddHandler}
              />
            </div>
          </div>
        )}
        {showOtherDocuments && (
          <div>
            <div className="flex-1 pb-2 mt-6 font-bold">Other Documents</div>
            <div>
              {otherDocs.map((doc) => (
                <UploadedFile
                  key={doc.url}
                  doc={doc}
                  deleteHandler={deleteHandler}
                />
              ))}
              <AddButton
                text={isUploadingOther ? "Uploading..." : "Upload File"}
                disabled={disableInputs}
                onClick={() =>
                  ensurePersonAvailable() &&
                  otherInput &&
                  otherInput.current &&
                  otherInput.current.click()
                }
              />
              <input
                className="hidden"
                type="file"
                ref={otherInput}
                name="_file_other"
                onChange={otherAddHandler}
              />
            </div>
          </div>
        )}
      </div>
      <div className="flex justify-end mt-8">
        <Button
          type={ButtonTypes.default}
          text={"Previous Step"}
          colorType={ButtonColorTypes.alternate}
          onClick={() => previousStep()}
        />
        <Button
          text={"Next Step"}
          colorType={ButtonColorTypes.custom}
          isRoundedFull={true}
          className="bg-orange-500 text-white rounded-full ml-auto w-56"
          onClick={handleNextStep}
          disabled={!isValid}
        />
      </div>
    </div>
  );
};
